import { P2GGrid } from '@parcel2go/core-ui';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { EditType } from './EditType';
import { P2GLink } from '@parcel2go/core-ui';

import { SettingsCache } from '~/lib/consts/settings-cache';
import { P2GText } from '@parcel2go/core-ui';
import { P2GBox } from '@parcel2go/core-ui';
import { P2GTooltip } from '@parcel2go/core-ui';
import { SvgEnquiryMoreInfo } from '@parcel2go/core-ui';
import { EventSections } from '../../../lib/events/EventLogger';
import ItemProtectionSummaryDrop from './ItemProtectionSummaryDrop';
import P2GContentDropDown from '../../P2GContentDropDown';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import CdnIconProxy from '~/apps/account-linking/components/CdnIconProxy';

interface props {
  item: SmartSendItemApiModel;
  edit?: (type: EditType) => void;
  section: string;
}

export default function ItemProtectionSummary(props: props) {
  const coverPercentage = props.item.State.validation.QuoteState.Quote.Options[0].CoverPercentage || 0;
  const covered = props.item.State.validation.QuoteState.Quote.Options[0].Covered || 0;
  const currencyCode = props.item.State.validation.QuoteState.Quote.CurrencyCode ?? props.item.CurrencyCode;
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { track } = useAnalytics();

  // const costToIncreaseCover = useMemo(() => {
  //   if (props.item.State.validation.QuoteState.Quote.Options[0]) {
  //     let ae = props.item.State.validation.QuoteState.Quote.Options[0].AvailableExtras;
  //     if (ae.find((x) => x.Id == ExtendedBaseCoverExtra)) {
  //       return ae.find((x) => x.Id == ExtendedBaseCoverExtra).Price;
  //     }
  //     if (ae.find((x) => x.Id == CoverExtra)) {
  //       return ae.find((x) => x.Id == CoverExtra).Price;
  //     }
  //   }
  //   return 0;
  // }, []);

  const onClick = () => {
    track({
      type: 'add_protection_click',
      payload: {
        ...GenerateBaseAnalyticsPayload([props.section, EventSections.SmartSendItem, EventSections.SmartSendItemFooter])
      }
    });

    props.edit('EXTRAS');
  };

  if (coverPercentage === 0) {
    return (
      <P2GGrid container wrap="nowrap" direction="row" gap={1} alignItems="center">
        <P2GGrid item>
          <P2GTooltip
            title={
              (
                <P2GText variant="body2">
                  <Trans
                    t={t}
                    i18nKey="NO_PARCEL_PROTECTION_TOOLTIP_TITLE"
                    defaults="Your parcel does not<br />have protection against<br />loss or damage"
                  />
                </P2GText>
              ) as any
            }
            children={
              <CdnIconProxy size="xs" palette="primary" src="/theme/icons/EnquiryMoreInfo.svg">
                <SvgEnquiryMoreInfo />
              </CdnIconProxy>
            }
          />
        </P2GGrid>
        <P2GGrid item>
          <P2GBox textAlign="left">
            <P2GText variant="body2" component="p" colour="error">
              <strong>{t('NO_PROTECTION', { defaultValue: 'No Protection' })}</strong>
            </P2GText>
            {SettingsCache.settings.Features.includes('InlineProtection') ? (
              <P2GContentDropDown
                control={<P2GLink variant="body2">{t('ADD_NOW_LINK', { defaultValue: 'Add Now' })}</P2GLink>}
              >
                <ItemProtectionSummaryDrop item={props.item} />
              </P2GContentDropDown>
            ) : (
              <P2GLink variant="body2" onClick={onClick}>
                {t('ADD_NOW_LINK', { defaultValue: 'Add Now' })}
              </P2GLink>
            )}
          </P2GBox>
        </P2GGrid>
      </P2GGrid>
    );
  }

  if (coverPercentage === 100) {
    return (
      <P2GGrid container wrap="nowrap" direction="row" gap={1} alignItems="center">
        <P2GGrid item>
          <P2GTooltip
            title={
              (
                <P2GText variant="body2">
                  <Trans
                    t={t}
                    i18nKey="FULL_PARCEL_PROTECTION_TOOLTIP_TITLE"
                    defaults="Your parcel is protected<br />against loss or damage"
                  />
                </P2GText>
              ) as any
            }
            children={
              <CdnIconProxy size="xs" palette="primary" src="/theme/icons/EnquiryMoreInfo.svg">
                <SvgEnquiryMoreInfo />
              </CdnIconProxy>
            }
          />
        </P2GGrid>
        <P2GGrid item>
          <P2GBox textAlign="left">
            <P2GText variant="body2" component="p" colour="primary">
              <strong style={{ color: '#629833' }}>{t('FULL_POTECTION', { defaultValue: 'Full Protection' })}</strong>
            </P2GText>
            <P2GLink variant="body2" onClick={onClick}>
              {t('EDIT_HERE_LINK', { defaultValue: 'Edit Here' })}
            </P2GLink>
          </P2GBox>
        </P2GGrid>
      </P2GGrid>
    );
  }

  return (
    <P2GGrid container wrap="nowrap" direction="row" gap={1} alignItems="center">
      <P2GGrid item>
        <P2GTooltip
          title={
            (
              <P2GText variant="body2">
                <Trans
                  t={t}
                  i18nKey="PARTIAL_PARCEL_PROTECTION_TOOLTIP_TITLE"
                  values={{ amount: toDisplayCurrency(currencyCode, covered) }}
                  defaults="Your parcel is protected<br />against loss or damage<br />up to the value of {{amount}}"
                />
              </P2GText>
            ) as any
          }
          children={
            <CdnIconProxy size="xs" palette="primary" src="/theme/icons/EnquiryMoreInfo.svg">
              <SvgEnquiryMoreInfo />
            </CdnIconProxy>
          }
        />
      </P2GGrid>
      <P2GGrid item>
        <P2GBox textAlign="left">
          <P2GText variant="body2" component="p" colour="error">
            <strong style={{ color: 'rgb(233 148 43)' }}>
              {t('NOT_FULLY_PROTECTED', { defaultValue: 'Not Fully Protected' })}
            </strong>
          </P2GText>
          {SettingsCache.settings.Features.includes('InlineProtection') ? (
            <P2GContentDropDown
              control={<P2GLink variant="body2">{t('ADD_MORE_LINK', { defaultValue: 'Add More' })}</P2GLink>}
            >
              <ItemProtectionSummaryDrop item={props.item} />
            </P2GContentDropDown>
          ) : (
            <P2GLink variant="body2" onClick={onClick}>
              {t('ADD_MORE_LINK', { defaultValue: 'Add More' })}
            </P2GLink>
          )}
        </P2GBox>
      </P2GGrid>
    </P2GGrid>
  );
}
